import { api } from "@redux/api";
import {
  AddNewPortfolioMutation,
  AddNewPortfolioMutationVariables,
  Community,
  GetCommunitiesForExistingPortfolioQuery,
  GetCommunitiesForExistingPortfolioQueryVariables,
  GetPortfoliosQuery,
  Portfolio,
} from "@graphql/api";
import {
  GetCommunityConfigurationQuery,
  GetCommunityConfigurationQueryVariables,
  GetRentManagerGeneralLedgerAccountsQuery,
  GetRentManagerGeneralLedgerAccountsQueryVariables,
  RentManagerGeneralLedgerAccount,
  SaveCommunityConfigurationMutation,
  SaveCommunityConfigurationMutationVariables,
  ToggleCommunityActiveStatusMutation,
  ToggleCommunityActiveStatusMutationVariables,
  CommunityConfiguration,
} from "@graphql/api";
import { GraphQLResult } from "@aws-amplify/api-graphql";
import { Tags } from "@redux/types";

export const portfoliosApi = api.injectEndpoints({
  endpoints: (build) => ({
    addPortfolio: build.mutation<boolean, AddNewPortfolioMutationVariables>({
      query: (data) => {
        return {
          operationName: "addNewPortfolio",
          data,
        };
      },
      transformResponse: (response: GraphQLResult<AddNewPortfolioMutation>) =>
        response?.data.addNewPortfolio,
      invalidatesTags: [{ type: Tags.Portfolio, id: "ALL" }],
    }),
    getCommunityConfiguration: build.query<
      CommunityConfiguration,
      GetCommunityConfigurationQueryVariables
    >({
      query: ({ portfolioId }) => ({
        operationName: "getCommunityConfiguration",
        data: { portfolioId },
      }),
      transformResponse: (
        response: GraphQLResult<GetCommunityConfigurationQuery>
      ) => response.data.getCommunityConfiguration,
      providesTags: (_result, _error, { portfolioId }) => [
        { type: Tags.Portfolio, id: portfolioId },
        { type: Tags.Community, id: `${portfolioId}#CONFIGURATION` },
      ],
    }),
    getRentManagerGeneralLedgerAccounts: build.query<
      RentManagerGeneralLedgerAccount[],
      GetRentManagerGeneralLedgerAccountsQueryVariables
    >({
      query: ({ locationId, portfolioId }) => ({
        operationName: "getRentManagerGeneralLedgerAccounts",
        data: { locationId, portfolioId },
      }),
      transformResponse: (
        response: GraphQLResult<GetRentManagerGeneralLedgerAccountsQuery>
      ) => response.data?.getRentManagerGeneralLedgerAccounts ?? [],
      providesTags: (_result, _error, { locationId, portfolioId }) => [
        {
          type: Tags.Portfolio,
          id: `${portfolioId}#RENT_MANAGER_LOCATION#${locationId}#GL_ACCOUNTS`,
        },
      ],
    }),
    saveCommunityConfiguration: build.mutation<
      SaveCommunityConfigurationMutation["saveCommunityConfiguration"],
      SaveCommunityConfigurationMutationVariables
    >({
      query: (data) => ({
        operationName: "saveCommunityConfiguration",
        data,
      }),
      transformResponse: (
        response: GraphQLResult<SaveCommunityConfigurationMutation>
      ) => response.data.saveCommunityConfiguration,
      invalidatesTags: (_result, _error, { configuration }) => [
        { type: Tags.Portfolio, id: configuration.portfolioId },
        { type: Tags.Portfolio, id: "ALL" },
        {
          type: Tags.Community,
          id: `${configuration.portfolioId}#CONFIGURATION`,
        },
      ],
    }),
    toggleCommunityActiveStatus: build.mutation<
      Community,
      ToggleCommunityActiveStatusMutationVariables
    >({
      query: (data) => ({
        operationName: "toggleCommunityActiveStatus",
        data,
      }),
      transformResponse: (
        response: GraphQLResult<ToggleCommunityActiveStatusMutation>
      ) => response.data.toggleCommunityActiveStatus,
      invalidatesTags: (_result, _error, { portfolioId, propertyId }) => [
        { type: Tags.Community, id: `${portfolioId}#${propertyId}` },
      ],
    }),
    getCommunitiesForExistingPortfolio: build.query<
      Community[],
      GetCommunitiesForExistingPortfolioQueryVariables
    >({
      query: ({ id }) => {
        return {
          operationName: "getCommunitiesForExistingPortfolio",
          data: { id },
        };
      },
      transformResponse: (
        response: GraphQLResult<GetCommunitiesForExistingPortfolioQuery>
      ) => response.data?.getCommunitiesForExistingPortfolio || [],
      providesTags: (res) =>
        res
          ? res.map(({ propertyId, portfolio: { id } }) => ({
              type: Tags.Community,
              id: `${id}#${propertyId}`,
            }))
          : [],
    }),
    getPortfolios: build.query<Portfolio[], void>({
      query: () => {
        return {
          operationName: "getPortfolios",
          data: null,
        };
      },
      transformResponse: (response: GraphQLResult<GetPortfoliosQuery>) =>
        response.data?.getPortfolios || [],
      providesTags: (res) =>
        res
          ? res
              .map(({ id }) => ({ type: Tags.Portfolio, id }))
              .concat([{ type: Tags.Portfolio, id: "ALL" }])
          : [],
    }),
  }),
});

export const {
  useAddPortfolioMutation,
  useGetCommunityConfigurationQuery,
  useGetRentManagerGeneralLedgerAccountsQuery,
  useSaveCommunityConfigurationMutation,
  useToggleCommunityActiveStatusMutation,
  useGetCommunitiesForExistingPortfolioQuery,
  useLazyGetCommunitiesForExistingPortfolioQuery,
  useGetPortfoliosQuery,
} = portfoliosApi;
